import { GA } from '@app/shared/pdf/ga-forms';
import { LogisticsCatalogs, fmtDate } from '../logistics.service';

/**
 * Formato de TRASPASO DE MATERIAL (reemplaza al Excel que se mandaba a DHL).
 * Mismo estilo que los formatos GA: barra azul, folio en recuadro, tabla de
 * piezas y firmas de quien entrega, quien recolecta (DHL) y quien recibe.
 */
export interface TransferPdfData {
  folio: string; date: string; status: string;
  origin: string; originAddress: string;
  destination: string; destinationAddress: string;
  request: string; guide: string; notes: string;
  items: { code: string; description: string; batch: string; expiration: string; tag: string; missing?: boolean }[];
}

const v = (x: any) => (x === null || x === undefined || String(x).trim() === '') ? ' ' : String(x);

function box(label: string, value: string, sub = ''): any {
  return {
    table: { widths: ['*'], body: [[{ stack: [
      { text: label, color: GA.label, fontSize: 7.5, bold: true },
      { text: v(value), fontSize: 9.5, bold: true, margin: [0, 2, 0, 0] },
      ...(sub ? [{ text: sub, fontSize: 7.5, color: '#444', margin: [0, 2, 0, 0] }] : []),
    ], margin: [4, 3, 4, 3] }]] },
    layout: { hLineWidth: () => 1.2, vLineWidth: () => 1.2, hLineColor: () => GA.navy, vLineColor: () => GA.navy },
  };
}

export function buildTransferDoc(d: TransferPdfData): any {
  const head = ['#', 'Código', 'Descripción', 'Lote', 'Caducidad', 'Etiqueta RFID', 'Recibido'].map(t => ({
    text: t, bold: true, color: GA.white, fillColor: GA.blue, fontSize: 7, alignment: t === '#' || t === 'Recibido' ? 'center' : 'left',
  }));
  const body: any[] = [head, ...d.items.map((it, i) => [
    { text: String(i + 1), fontSize: 7, alignment: 'center' },
    { text: v(it.code), fontSize: 7, color: GA.label, bold: true },
    { text: v(it.description), fontSize: 7, color: GA.label },
    { text: v(it.batch), fontSize: 7 },
    { text: v(it.expiration), fontSize: 7 },
    { text: v(it.tag), fontSize: 6.5 },
    { text: it.missing ? 'FALTANTE' : ' ', fontSize: 6.5, bold: true, color: '#c62828', alignment: 'center' },
  ])];
  for (let i = d.items.length; i < 12; i++) body.push([' ', '', '', '', '', '', '']);

  return {
    pageSize: 'LETTER',
    pageMargins: [28, 104, 28, 86],
    defaultStyle: { fontSize: 8, color: '#111' },
    info: { title: `Traspaso ${d.folio}` },
    background: () => [
      { canvas: [
        { type: 'rect', x: 0, y: 0, w: 612, h: 40, color: GA.blue },
        { type: 'rect', x: 488, y: 48, w: 106, h: 30, lineWidth: 1.5, lineColor: GA.navy },
      ]},
      { text: 'TRASPASO DE MATERIAL ENTRE CUENTAS', absolutePosition: { x: 16, y: 13 }, color: GA.white, bold: true, fontSize: 12.5 },
      { text: 'Medtronic', absolutePosition: { x: 512, y: 9 }, color: GA.white, bold: true, fontSize: 17 },
      { text: 'FECHA:', absolutePosition: { x: 300, y: 58 }, color: GA.label, fontSize: 10 },
      { text: d.date, absolutePosition: { x: 344, y: 59 }, bold: true, fontSize: 9.5 },
      { text: 'FOLIO:', absolutePosition: { x: 446, y: 58 }, color: GA.label, fontSize: 10 },
      { text: d.folio, absolutePosition: { x: 490, y: 57 }, bold: true, fontSize: 11, width: 102, alignment: 'center' },
      { text: `Estado: ${d.status}`, absolutePosition: { x: 28, y: 58 }, color: GA.label, fontSize: 9 },
    ],
    footer: () => ({
      margin: [28, 22, 28, 0],
      columns: ['Entrega (origen)', 'Recolecta (DHL)', 'Recibe (destino)'].map(t => ({
        stack: [
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 160, y2: 0, lineWidth: 1, lineColor: GA.navy }] },
          { text: 'Nombre, firma y fecha', color: '#666', fontSize: 6.5, margin: [0, 2, 0, 0] },
          { text: t, color: GA.label, fontSize: 8, bold: true, margin: [0, 1, 0, 0] },
        ], alignment: 'center', width: '*',
      })),
      columnGap: 14,
    }),
    content: [
      { columns: [
        { width: '*', stack: [box('ORIGEN (RECOLECTAR EN)', d.origin, d.originAddress)] },
        { width: '*', stack: [box('DESTINO (ENTREGAR EN)', d.destination, d.destinationAddress)] },
      ], columnGap: 10 },
      { text: ' ', fontSize: 5 },
      { columns: [
        { width: '*', stack: [box('SOLICITUD DE MATERIAL', d.request || '—')] },
        { width: '*', stack: [box('GUÍA DHL', d.guide || '')] },
        { width: '*', stack: [box('PIEZAS', String(d.items.length))] },
      ], columnGap: 10 },
      { text: ' ', fontSize: 6 },
      { text: 'Material', bold: true, color: GA.blue, fontSize: 11, margin: [0, 0, 0, 3] },
      {
        table: { headerRows: 1, widths: [16, 58, '*', 58, 52, 112, 44], body },
        layout: {
          hLineWidth: () => 0.5, vLineWidth: () => 0.5, hLineColor: () => GA.grid, vLineColor: () => GA.grid,
          paddingLeft: () => 3, paddingRight: () => 3, paddingTop: () => 2, paddingBottom: () => 2,
        },
      },
      { text: ' ', fontSize: 6 },
      { table: { widths: ['*'], body: [[{ stack: [
          { text: 'Observaciones:', color: GA.label, fontSize: 9, margin: [0, 0, 0, 3] },
          { text: v(d.notes), fontSize: 8 },
          { text: ' ', fontSize: 10 },
        ], margin: [4, 3, 4, 3] }]] },
        layout: { hLineWidth: () => 1.5, vLineWidth: () => 1.5, hLineColor: () => GA.navy, vLineColor: () => GA.navy } },
    ],
  };
}

/** Datos del formato a partir del traspaso (con nombres, no ids). */
export function transferPdfData(t: any, cat: LogisticsCatalogs, requestFolio = ''): TransferPdfData {
  return {
    folio: t.folio ?? '', status: t.status ?? '',
    date: fmtDate(t.shippedOn || t.createdOn, false),
    origin: cat.warehouseLabelById(t.originWarehouse), originAddress: cat.placeAddress(t.originPlace),
    destination: cat.warehouseLabelById(t.destinationWarehouse), destinationAddress: cat.placeAddress(t.destinationPlace),
    request: requestFolio, guide: t.dhlGuide ?? '', notes: t.notes ?? '',
    items: (t.items ?? []).map((i: any) => ({
      code: i.code ?? '', description: i.description ?? '', batch: i.batch ?? '',
      expiration: fmtDate(i.expiration, false), tag: i.tagRfid ?? '', missing: !!i.missing,
    })),
  };
}
